import { useState, useEffect, useMemo } from 'react';
import CollectVariableTreeNode from '../types/BotBuilder/CollectVariableTreeNode';
import Slot from '../types/BotBuilder/Slot';
import SlotValueType from '../types/BotBuilder/SlotValueType';
import InlineEnumeratedSlotValue from '../types/BotBuilder/InlineEnumeratedSlotValue';

interface UseCollectVariableInitialisationReturn {
  variableMode: 'existing' | 'new';
  setVariableMode: (mode: 'existing' | 'new') => void;
  newVariableName: string;
  setNewVariableName: (name: string) => void;
  newSlotType: SlotValueType | null;
  setNewSlotType: (type: SlotValueType | null) => void;
  enumeratedValues: InlineEnumeratedSlotValue[];
  setEnumeratedValues: (values: InlineEnumeratedSlotValue[]) => void;
  selectedSlot: Slot | null;
  setSelectedSlot: (slot: Slot | null) => void;
}

interface InitialisationData {
  mode: 'existing' | 'new';
  variableName: string;
  slotType: SlotValueType | null;
  enumeratedValues: InlineEnumeratedSlotValue[];
  selectedSlot: Slot | null;
}

function useCollectVariableInitialisation(
  collectVariableNode: CollectVariableTreeNode | null,
  slots: Slot[]
): UseCollectVariableInitialisationReturn {
  const [variableMode, setVariableMode] = useState<'existing' | 'new'>('new');
  const [newVariableName, setNewVariableName] = useState<string>('');
  const [newSlotType, setNewSlotType] = useState<SlotValueType | null>(null);
  const [enumeratedValues, setEnumeratedValues] = useState<
    InlineEnumeratedSlotValue[]
  >([{ value: '', synonyms: [] }]);
  const [selectedSlot, setSelectedSlot] = useState<Slot | null>(null);

  const initialisationData = useMemo((): InitialisationData => {
    if (!collectVariableNode) {
      return {
        mode: 'new',
        variableName: '',
        slotType: null,
        enumeratedValues: [{ value: '', synonyms: [] }],
        selectedSlot: null
      };
    }

    const hasSlotType = !!collectVariableNode.slotType;
    const isEnumerated =
      collectVariableNode.slotValueType === SlotValueType.ENUMERATED_VALUE;

    if (hasSlotType) {
      const foundSlot = slots.find(
        (slot) => slot.slotName === collectVariableNode.slotType
      );
      return {
        mode: 'existing',
        variableName: collectVariableNode.slotName ?? '',
        slotType: collectVariableNode.slotValueType,
        enumeratedValues: [{ value: '', synonyms: [] }],
        selectedSlot: foundSlot ?? null
      };
    }

    if (isEnumerated && !hasSlotType) {
      const values =
        collectVariableNode.inlineEnumeratedValues ||
        collectVariableNode.slotConditionals
          ?.map((c) => c.slotValue)
          .filter(Boolean)
          .map((value) => ({ value, synonyms: [] })) ||
        [];
      return {
        mode: 'new',
        variableName: collectVariableNode.slotName ?? '',
        slotType: collectVariableNode.slotValueType,
        enumeratedValues:
          values.length > 0 ? values : [{ value: '', synonyms: [] }],
        selectedSlot: null
      };
    }

    if (collectVariableNode.slotValueType) {
      return {
        mode: 'new',
        variableName: collectVariableNode.slotName ?? '',
        slotType: collectVariableNode.slotValueType,
        enumeratedValues: [{ value: '', synonyms: [] }],
        selectedSlot: null
      };
    }

    return {
      mode: 'new',
      variableName: '',
      slotType: null,
      enumeratedValues: [{ value: '', synonyms: [] }],
      selectedSlot: null
    };
  }, [collectVariableNode, slots]);

  useEffect(() => {
    setVariableMode(initialisationData.mode);
    setNewVariableName(initialisationData.variableName);
    setNewSlotType(initialisationData.slotType);
    setEnumeratedValues(initialisationData.enumeratedValues);
    setSelectedSlot(initialisationData.selectedSlot);
  }, [initialisationData]);

  return {
    variableMode,
    setVariableMode,
    newVariableName,
    setNewVariableName,
    newSlotType,
    setNewSlotType,
    enumeratedValues,
    setEnumeratedValues,
    selectedSlot,
    setSelectedSlot
  };
}

export default useCollectVariableInitialisation;
